import { countryFromTimezone } from './timezoneCountry';

/**
 * Locales válidos no formato language-REGION (ex: pt-BR, en-US).
 * Usado para normalizar valores como "pt" ou "en" para o formato completo.
 *
 * en-CA: Canadá em inglês (billing CAD).
 * en-GE: Geórgia em inglês (billing GEL) — legado / opcional.
 * ka / ka-GE: Geórgia em georgiano (LPs /lp/.../ge).
 */
const VALID_LOCALES = [
  'pt-BR', 'pt-PT', 'en-US', 'en-GB', 'en-CA', 'en-GE', 'ka-GE',
  'es-ES', 'es-MX', 'es-CL', 'es-CO', 'es-PE',
  'fr-FR', 'de-DE', 'it-IT', 'pl-PL', 'nl-NL',
];

/**
 * Mapeamento de códigos de idioma simples para locale completo padrão.
 * Idioma "pt" sem região continua Brasil (mercado principal).
 * "ka" (georgiano) → ka-GE (mercado Geórgia + LPs).
 */
const LANGUAGE_TO_LOCALE = {
  pt: 'pt-BR',
  en: 'en-US',
  es: 'es-ES',
  fr: 'fr-FR',
  de: 'de-DE',
  it: 'it-IT',
  pl: 'pl-PL',
  nl: 'nl-NL',
  ka: 'ka-GE',
};

/** Tags regionais canônicas (antes do fallback de idioma). */
const REGIONAL_LOCALE_ALIASES = {
  'ka-GE': 'ka-GE',
  'en-GE': 'en-GE',
  'en-CA': 'en-CA',
};

/**
 * ISO 639-1. Espelha backend/dashboard/locale_prefs.py.
 * Línguas fora dos mercados de billing são gravadas, não convertidas em pt-BR.
 */
export const ISO_639_1_CODES = (
  'aa ab ae af ak am an ar as av ay az ba be bg bi bm bn bo br bs ca ce ch co cr cs cu cv cy ' +
  'da de dv dz ee el en eo es et eu fa ff fi fj fo fr fy ga gd gl gn gu gv ha he hi ho hr ht ' +
  'hu hy hz ia id ie ig ii ik io is it iu ja jv ka kg ki kj kk kl km kn ko kr ks ku kv kw ky ' +
  'la lb lg li ln lo lt lu lv mg mh mi mk ml mn mr ms mt my na nb nd ne ng nl nn no nr nv ny ' +
  'oc oj om or os pa pi pl ps pt qu rm rn ro ru rw sa sc sd se sg si sk sl sm sn so sq sr ss ' +
  'st su sv sw ta te tg th ti tk tl tn to tr ts tt tw ty ug uk ur uz ve vi vo wa wo xh yi yo ' +
  'za zh zu'
).split(' ');

/** fil = Filipino. bh/sh ainda aparecem em alguns navegadores. */
export const EXTRA_SPOKEN_LANGS = ['fil', 'bh', 'sh'];

const SPOKEN_LANGS = new Set([...ISO_639_1_CODES, ...EXTRA_SPOKEN_LANGS]);

/** Códigos legados de Java/Android → ISO atual. */
const LANG_ALIASES = {
  iw: 'he',
  in: 'id',
  ji: 'yi',
  mo: 'ro',
};

const LANG_RE = /^[a-z]{2,3}$/;
const SCRIPT_RE = /^[A-Za-z]{4}$/;
const REGION_RE = /^[A-Za-z]{2}$/;

function splitLocale(locale) {
  if (!locale || typeof locale !== 'string') return ['', null];
  const parts = locale.trim().replace(/_/g, '-').split('-').filter(Boolean);
  if (parts.length === 0) return ['', null];
  let lang = parts[0].toLowerCase();
  if (!LANG_RE.test(lang)) return ['', null];
  lang = LANG_ALIASES[lang] || lang;
  let rest = parts.slice(1);
  if (rest.length > 0 && SCRIPT_RE.test(rest[0])) {
    rest = rest.slice(1);
  }
  const region = rest.length > 0 && REGION_RE.test(rest[0]) ? rest[0].toUpperCase() : null;
  return [lang, region];
}

/**
 * ISO country → preferred_locale do perfil (campanhas internacionais).
 */
export const COUNTRY_TO_PREFERRED_LOCALE = {
  BR: 'pt-BR',
  US: 'en-US',
  CA: 'en-CA',
  GE: 'ka-GE',
  GB: 'en-GB',
  UK: 'en-GB',
  ES: 'es-ES',
  MX: 'es-MX',
  CL: 'es-CL',
  CO: 'es-CO',
  PE: 'es-PE',
  NL: 'nl-NL',
  PT: 'pt-PT',
  FR: 'fr-FR',
  DE: 'de-DE',
  IT: 'it-IT',
  PL: 'pl-PL',
};

/**
 * Canonicaliza tag BCP-47: pt-pt / pt_PT → pt-PT; zh-Hans-CN → zh-CN.
 * Região numérica (es-419) é descartada para o idioma cair no mercado conhecido.
 * @param {string} locale
 * @returns {string}
 */
export function canonicalizeLocaleTag(locale) {
  const [lang, region] = splitLocale(locale);
  if (!lang) return '';
  return region ? `${lang}-${region}` : lang;
}

/**
 * Normaliza um locale para gravar em profiles.preferred_locale.
 * - Mercados conhecidos permanecem (pt-PT, en-CA, ka-GE, es-MX, …).
 * - Idioma de mercado sem região reconhecida vai para o locale desse mercado
 *   (en-AU → en-US, es → es-ES): o idioma falado não muda.
 * - Língua sem país vira idioma-REGIÃO (pt → pt-BR, en → en-US, ja → ja-JP).
 * - Nunca grava só "pt" ou "en".
 * - Tag inválida volta pt-BR.
 * @param {string} locale - Locale do navegador ou do banco
 * @returns {string} Locale normalizado
 */
export function normalizeLocale(locale) {
  if (!locale || typeof locale !== 'string') {
    return 'pt-BR';
  }
  const [lang, region] = splitLocale(locale);
  if (!lang) return 'pt-BR';

  const canonical = region ? `${lang}-${region}` : lang;

  if (REGIONAL_LOCALE_ALIASES[canonical]) {
    return REGIONAL_LOCALE_ALIASES[canonical];
  }

  if (VALID_LOCALES.includes(canonical)) {
    return canonical;
  }

  if (LANGUAGE_TO_LOCALE[lang]) {
    return LANGUAGE_TO_LOCALE[lang];
  }

  if (SPOKEN_LANGS.has(lang)) {
    if (region) return `${lang}-${region}`;
    const defaultRegion = DEFAULT_REGION[lang];
    if (defaultRegion) return `${lang}-${defaultRegion}`;
  }

  return 'pt-BR';
}

/**
 * Resolve preferred_locale a partir de um código ISO de país (ex: da LP).
 * @param {string} countryCode
 * @returns {string|null}
 */
export function preferredLocaleFromCountryCode(countryCode) {
  if (!countryCode || typeof countryCode !== 'string') return null;
  const code = countryCode.trim().toUpperCase();
  return COUNTRY_TO_PREFERRED_LOCALE[code] || null;
}

/** Região padrão (CLDR) para não gravar só "pt" ou "ja". */
const DEFAULT_REGION = {
  aa: 'ET', ab: 'GE', ae: 'IR', af: 'ZA', ak: 'GH', am: 'ET',
  an: 'ES', ar: 'EG', as: 'IN', av: 'RU', ay: 'BO', az: 'AZ',
  ba: 'RU', be: 'BY', bg: 'BG', bh: 'IN', bi: 'VU', bm: 'ML',
  bn: 'BD', bo: 'CN', br: 'FR', bs: 'BA', ca: 'ES', ce: 'RU',
  ch: 'GU', co: 'FR', cr: 'CA', cs: 'CZ', cu: 'RU', cv: 'RU',
  cy: 'GB', da: 'DK', de: 'DE', dv: 'MV', dz: 'BT', ee: 'GH',
  el: 'GR', en: 'US', eo: 'US', es: 'ES', et: 'EE', eu: 'ES',
  fa: 'IR', ff: 'SN', fi: 'FI', fil: 'PH', fj: 'FJ', fo: 'FO',
  fr: 'FR', fy: 'NL', ga: 'IE', gd: 'GB', gl: 'ES', gn: 'PY',
  gu: 'IN', gv: 'IM', ha: 'NG', he: 'IL', hi: 'IN', ho: 'PG',
  hr: 'HR', ht: 'HT', hu: 'HU', hy: 'AM', hz: 'NA', ia: 'FR',
  id: 'ID', ie: 'EE', ig: 'NG', ii: 'CN', ik: 'US', io: 'FR',
  is: 'IS', it: 'IT', iu: 'CA', ja: 'JP', jv: 'ID', ka: 'GE',
  kg: 'CD', ki: 'KE', kj: 'NA', kk: 'KZ', kl: 'GL', km: 'KH',
  kn: 'IN', ko: 'KR', kr: 'NG', ks: 'IN', ku: 'TR', kv: 'RU',
  kw: 'GB', ky: 'KG', la: 'VA', lb: 'LU', lg: 'UG', li: 'NL',
  ln: 'CD', lo: 'LA', lt: 'LT', lu: 'CD', lv: 'LV', mg: 'MG',
  mh: 'MH', mi: 'NZ', mk: 'MK', ml: 'IN', mn: 'MN', mr: 'IN',
  ms: 'MY', mt: 'MT', my: 'MM', na: 'NR', nb: 'NO', nd: 'ZW',
  ne: 'NP', ng: 'NA', nl: 'NL', nn: 'NO', no: 'NO', nr: 'ZA',
  nv: 'US', ny: 'MW', oc: 'FR', oj: 'CA', om: 'ET', or: 'IN',
  os: 'GE', pa: 'IN', pi: 'GB', pl: 'PL', ps: 'AF', pt: 'BR',
  qu: 'PE', rm: 'CH', rn: 'BI', ro: 'RO', ru: 'RU', rw: 'RW',
  sa: 'IN', sc: 'IT', sd: 'PK', se: 'NO', sg: 'CF', sh: 'RS',
  si: 'LK', sk: 'SK', sl: 'SI', sm: 'WS', sn: 'ZW', so: 'SO',
  sq: 'AL', sr: 'RS', ss: 'ZA', st: 'ZA', su: 'ID', sv: 'SE',
  sw: 'TZ', ta: 'IN', te: 'IN', tg: 'TJ', th: 'TH', ti: 'ET',
  tk: 'TM', tl: 'PH', tn: 'ZA', to: 'TO', tr: 'TR', ts: 'ZA',
  tt: 'RU', tw: 'GH', ty: 'PF', ug: 'CN', uk: 'UA', ur: 'PK',
  uz: 'UZ', ve: 'ZA', vi: 'VN', vo: 'DE', wa: 'BE', wo: 'SN',
  xh: 'ZA', yi: 'UA', yo: 'NG', za: 'CN', zh: 'CN', zu: 'ZA',
};

function browserLanguageCandidates() {
  if (typeof navigator === 'undefined') return [];
  const list = Array.isArray(navigator.languages) ? [...navigator.languages] : [];
  const primary = navigator.language || navigator.userLanguage;
  if (primary) list.push(primary);
  return list.filter(Boolean);
}

/**
 * Região detectada pelo fuso, só quando o idioma bate com o país.
 * Inglês na Geórgia fica en-GE; o mapa de país da LP usa ka-GE.
 * @param {string} lang
 * @param {string|null} country
 * @returns {string|null}
 */
function localeFromLanguageAndCountry(lang, country) {
  if (!lang || !country) return null;
  if (lang === 'en' && country === 'GE') return 'en-GE';
  const mapped = COUNTRY_TO_PREFERRED_LOCALE[country];
  if (mapped && mapped.split('-')[0] === lang) return mapped;
  return null;
}

/**
 * Detecta o locale a partir de uma tag do navegador.
 * Se a tag já traz região, ela vale. Se traz só o idioma, o fuso escolhe o país.
 * Sem fuso compatível, o mercado padrão daquele idioma é o último recurso.
 * Tag que não é língua devolve null para o próximo candidato.
 * @param {string} language
 * @param {string} timezone
 * @returns {string|null}
 */
function localeFromDetectedLanguage(language, timezone) {
  const [lang, region] = splitLocale(language);
  if (!lang) return null;
  if (!SPOKEN_LANGS.has(lang)) return null;

  if (!region) {
    const fromTimezone = localeFromLanguageAndCountry(lang, countryFromTimezone(timezone));
    if (fromTimezone) return fromTimezone;
  }

  const normalized = normalizeLocale(region ? `${lang}-${region}` : lang);
  if (lang !== 'pt' && normalized === 'pt-BR') return null;
  return normalized;
}

/**
 * Locale do perfil quando não há landing.
 * Lê navigator.languages. Não usa o idioma da página /register (só pt ou en).
 * pt-BR só se nenhum candidato for uma língua.
 *
 * @param {{ language?: string, languages?: string[], timezone?: string }} [opts]
 * @returns {string}
 */
export function resolvePreferredLocale(opts = {}) {
  const timezone = opts.timezone || '';
  let candidates;
  if (opts.language) {
    candidates = [opts.language];
  } else if (Array.isArray(opts.languages)) {
    candidates = opts.languages;
  } else {
    candidates = browserLanguageCandidates();
  }

  for (const language of candidates) {
    const resolved = localeFromDetectedLanguage(language, timezone);
    if (resolved) return resolved;
  }
  return 'pt-BR';
}

/**
 * Locale gravado no cadastro.
 * 1. Landing (preferredLocale da LP).
 * 2. Idioma do navegador + fuso.
 * 3. pt-BR.
 * A UI de /register não entra: a página só existe em português ou inglês.
 *
 * @param {{ landingLocale?: string, timezone?: string, languages?: string[] }} [opts]
 * @returns {string}
 */
export function resolveSignupPreferredLocale(opts = {}) {
  const landing = opts.landingLocale ? String(opts.landingLocale).trim() : '';
  if (landing) {
    const [lang] = splitLocale(landing);
    const fromLanding = normalizeLocale(landing);
    if (lang && (lang === 'pt' || fromLanding !== 'pt-BR')) {
      return fromLanding;
    }
  }
  return resolvePreferredLocale({
    timezone: opts.timezone || '',
    languages: opts.languages,
  });
}

/**
 * Locale representativo do bundle de UI carregado (só existem pt-BR e en completos).
 * Usado pelo seletor rápido do header, que oferece apenas duas opções e ficaria
 * sem nenhuma marcada para locales como pt-PT, en-US ou es-ES.
 * @param {string} [locale]
 * @returns {'pt-BR'|'en-US'}
 */
export function resolveUiLanguageChoice(locale) {
  const canonical = canonicalizeLocaleTag(locale || '');
  const lang = canonical.split('-')[0]?.toLowerCase();
  return lang === 'pt' ? 'pt-BR' : 'en-US';
}

/**
 * Idioma de UI do funil de cadastro a partir do preferred_locale da LP.
 * ka / ka-GE → 'ka' (overlay georgiano). Outros mercados: null (não força).
 * @param {string} [preferredLocale]
 * @returns {'ka'|null}
 */
export function resolveSignupUiLocale(preferredLocale) {
  const canonical = canonicalizeLocaleTag(preferredLocale || '');
  const lang = canonical.split('-')[0]?.toLowerCase();
  if (lang === 'ka') return 'ka';
  return null;
}

/**
 * Idioma da tela para o locale que o cadastro grava.
 * ka-GE usa o overlay `ka`. Os outros mercados usam o próprio locale
 * (en-US, nl-NL, pt-BR), para um cookie antigo não deixar a tela em português.
 * @param {string} [preferredLocale]
 * @returns {string|null}
 */
export function resolveAccountUiLocale(preferredLocale) {
  const canonical = canonicalizeLocaleTag(preferredLocale || '');
  const lang = canonical.split('-')[0]?.toLowerCase();
  if (!lang) return null;
  if (lang === 'ka') return 'ka';
  return normalizeLocale(preferredLocale);
}

/**
 * Idioma dos prompts / protocolo agent-facing no chat: pt para português; en para qualquer outro locale.
 * @param {string} [locale]
 * @returns {'pt'|'en'}
 */
export function resolvePromptLang(locale) {
  if (!locale || typeof locale !== 'string') {
    return 'pt';
  }
  const trimmed = locale.trim();
  if (!trimmed) {
    return 'pt';
  }
  const primary = trimmed.split(/[-_]/)[0].toLowerCase();
  return primary === 'pt' ? 'pt' : 'en';
}

/**
 * Seletor rápido do header só distingue português e inglês.
 * Se a pessoa já está nesse idioma, mantém a região gravada (pt-PT, en-CA, en-GB).
 * Trocar de idioma grava o locale do botão (pt-BR ou en-US).
 * @param {string} currentLocale
 * @param {string} uiChoice
 * @returns {string}
 */
export function preferredLocaleForUiChoice(currentLocale, uiChoice) {
  const choice = normalizeLocale(uiChoice);
  if (!currentLocale) return choice;
  const current = normalizeLocale(currentLocale);
  const choiceLang = choice.split('-')[0];
  const currentLang = current.split('-')[0];
  if (currentLang && currentLang === choiceLang) return current;
  return choice;
}

/** Locais de mercado: o valor gravado também define moeda e país de billing. */
export const MARKET_PROFILE_LOCALES = [
  { value: 'pt-BR', label: '🇧🇷 Português (Brasil)' },
  { value: 'pt-PT', label: '🇵🇹 Português (Portugal)' },
  { value: 'en-US', label: '🇺🇸 English (United States)' },
  { value: 'en-CA', label: '🇨🇦 English (Canada)' },
  { value: 'es-ES', label: '🇪🇸 Español (España)' },
  { value: 'es-MX', label: '🇲🇽 Español (México)' },
  { value: 'fr-FR', label: '🇫🇷 Français (France)' },
  { value: 'de-DE', label: '🇩🇪 Deutsch (Deutschland)' },
  { value: 'it-IT', label: '🇮🇹 Italiano (Italia)' },
  { value: 'en-GB', label: '🇬🇧 English (United Kingdom)' },
  { value: 'en-GE', label: '🇬🇪 English (Georgia)' },
  { value: 'es-CL', label: '🇨🇱 Español (Chile)' },
  { value: 'es-CO', label: '🇨🇴 Español (Colombia)' },
  { value: 'es-PE', label: '🇵🇪 Español (Perú)' },
  { value: 'pl-PL', label: '🇵🇱 Polski (Polska)' },
  { value: 'nl-NL', label: '🇳🇱 Nederlands (Nederland)' },
  { value: 'ka-GE', label: '🇬🇪 ქართული (საქართველო)' },
];

const MARKET_LANGS = new Set(MARKET_PROFILE_LOCALES.map((option) => option.value.split('-')[0]));

/**
 * Nome do idioma para o select do perfil.
 * @param {string} tag
 * @returns {string}
 */
export function spokenLocaleLabel(tag) {
  try {
    const names = new Intl.DisplayNames(['pt-BR'], { type: 'language' });
    const label = names.of(tag);
    if (label && label.toLowerCase() !== String(tag).toLowerCase()) {
      return label.charAt(0).toUpperCase() + label.slice(1);
    }
  } catch (_err) {
    /* Intl ausente: mostra a tag. */
  }
  return tag;
}

let cachedExtraLocales = null;

function extraSpokenLocaleOptions() {
  if (cachedExtraLocales) return cachedExtraLocales;
  const codes = [...ISO_639_1_CODES, ...EXTRA_SPOKEN_LANGS].filter((code) => !MARKET_LANGS.has(code));
  const seen = new Set();
  cachedExtraLocales = codes
    .map((code) => {
      const value = normalizeLocale(code);
      return { value, label: spokenLocaleLabel(value) };
    })
    .filter((option) => {
      if (!option.value.includes('-') || seen.has(option.value)) return false;
      seen.add(option.value);
      return true;
    })
    .sort((a, b) => a.label.localeCompare(b.label, 'pt'));
  return cachedExtraLocales;
}

/**
 * Opções do select de idioma do perfil.
 * Mercados de billing primeiro; depois as demais línguas ISO.
 * Se o valor já salvo não estiver na lista (ja-JP, ar-SA), ele entra no topo.
 * @param {string} [current]
 * @returns {{ value: string, label: string }[]}
 */
export function profileLocaleOptions(current) {
  const options = [...MARKET_PROFILE_LOCALES, ...extraSpokenLocaleOptions()];
  if (current && !options.some((option) => option.value === current)) {
    return [{ value: current, label: spokenLocaleLabel(current) }, ...options];
  }
  return options;
}
